import React, { useState } from 'react';

const rolesData = [
  {
    title: 'Chief Audit Executive (CAE) / Head of Internal Audit',
    description: `Role Overview:
The CAE is responsible for the overall governance and strategic direction of the internal audit function. This executive role ensures that the organization’s internal controls, risk management, and governance processes are robust and effective.

Key Responsibilities:
- Develop and implement the internal audit strategy and annual audit plan based on comprehensive risk assessments.
- Provide independent assurance to the Audit Committee and Board of Directors regarding the effectiveness of internal controls.
- Oversee high-level audits and ensure alignment with the organization’s strategic objectives.
- Communicate audit findings, risks, and recommendations to senior management and the board.
- Lead and mentor the entire internal audit team.

Requirements & Skills:
- Extensive experience in audit, risk management, and corporate governance.
- Professional certifications such as CIA, CPA, or equivalent.
- Strong leadership, strategic thinking, and communication skills.`
  },
  {
    title: 'Deputy Chief Audit Executive (DCAE)',
    description: `Role Overview:
The DCAE assists the CAE in managing the internal audit function and may serve as the acting head in the CAE’s absence.

Key Responsibilities:
- Support the CAE in the development and execution of the audit strategy.
- Oversee day-to-day operations and ensure adherence to audit standards.
- Coordinate cross-functional audits and facilitate effective communication between audit teams and business units.
- Help review audit results and ensure timely follow-up on recommendations.

Requirements & Skills:
- Strong background in auditing and risk management.
- Proven leadership and project management skills.
- Relevant professional certifications (e.g., CIA, CPA).`
  },
  {
    title: 'Director of Internal Audit',
    description: `Role Overview:
The Director of Internal Audit manages and directs the internal audit activities across the organization, ensuring that audit programs are effectively executed and aligned with corporate objectives.

Key Responsibilities:
- Lead the development and continuous improvement of audit methodologies.
- Coordinate the planning, execution, and reporting of audits across various business units.
- Identify key risks and work with management to implement corrective actions.
- Ensure compliance with both internal policies and external regulatory requirements.
- Act as a senior advisor to management on emerging risks and control issues.

Requirements & Skills:
- Significant experience in internal auditing and business process improvement.
- Excellent analytical, communication, and leadership skills.
- Professional certifications such as CIA or CPA are highly preferred.`
  },
  {
    title: 'Audit Manager',
    description: `Role Overview:
The Audit Manager supervises audit teams during fieldwork and is responsible for ensuring that audit engagements are completed efficiently and effectively.

Key Responsibilities:
- Plan and coordinate audit assignments, including scope definition, resource allocation, and scheduling.
- Supervise and mentor audit staff during audit engagements.
- Evaluate audit findings, prepare detailed reports, and present recommendations to management.
- Maintain communication with business unit leaders to clarify audit issues and follow up on remediation efforts.

Requirements & Skills:
- Strong experience in audit planning and execution.
- Proven team leadership and project management abilities.
- Excellent communication and problem-solving skills.
- Professional certifications (e.g., CIA, CPA) are advantageous.`
  },
  {
    title: 'Senior Internal Auditor',
    description: `Role Overview:
The Senior Internal Auditor leads complex audit engagements, performs in-depth risk assessments, and ensures thorough testing of internal controls.

Key Responsibilities:
- Execute detailed audit programs for high-risk areas.
- Analyze business processes, identify control weaknesses, and propose improvements.
- Prepare comprehensive audit reports with actionable recommendations.
- Provide guidance and support to junior auditors, fostering skill development.

Requirements & Skills:
- Extensive experience in internal auditing with a strong analytical background.
- Ability to manage multiple audit projects simultaneously.
- Familiarity with industry standards and risk management practices.
- Certification such as CIA or CPA is typically required.`
  },
  {
    title: 'Internal Auditor (Staff/Junior Auditor)',
    description: `Role Overview:
The Internal Auditor conducts fieldwork to test the adequacy and effectiveness of internal controls and assists in various audit assignments.

Key Responsibilities:
- Perform audit testing and document evidence related to financial, operational, and compliance controls.
- Analyze data to identify potential areas of risk or control weakness.
- Assist in the preparation of audit reports and recommendations for management.
- Collaborate with audit team members to ensure audit tasks are completed efficiently.

Requirements & Skills:
- Bachelor’s degree in accounting, finance, or a related field.
- Strong analytical, organizational, and communication skills.
- Familiarity with audit procedures and a willingness to learn.
- Entry-level certifications (or progress toward CIA or CPA) are beneficial.`
  },
  {
    title: 'IT Auditor / IT Audit Specialist',
    description: `Role Overview:
The IT Auditor focuses on the evaluation of the organization’s information technology systems and related controls, ensuring data integrity and cybersecurity.

Key Responsibilities:
- Conduct audits of IT systems, applications, and infrastructure to assess security and compliance.
- Evaluate IT controls, cybersecurity measures, and data management practices.
- Identify vulnerabilities and recommend measures to mitigate IT-related risks.
- Collaborate with IT and cybersecurity teams to ensure that audit findings are effectively addressed.

Requirements & Skills:
- In-depth understanding of IT systems, cybersecurity, and related regulatory requirements.
- Technical proficiency in data analytics and IT audit tools.
- Certifications such as CISA (Certified Information Systems Auditor) are typically required.
- Strong problem-solving and communication skills.`
  },
  {
    title: 'Compliance Auditor',
    description: `Role Overview:
The Compliance Auditor ensures that the organization adheres to external regulations and internal policies, minimizing the risk of legal and regulatory penalties.

Key Responsibilities:
- Review processes and procedures to verify compliance with regulatory standards and internal policies.
- Identify areas of non-compliance and propose corrective actions.
- Develop and implement compliance audit programs.
- Stay informed about regulatory changes and industry best practices.

Requirements & Skills:
- Detailed knowledge of regulatory frameworks relevant to the industry.
- Strong analytical skills and attention to detail.
- Excellent communication abilities to report findings to management.
- Relevant compliance or audit certifications are preferred.`
  },
  {
    title: 'Forensic Auditor',
    description: `Role Overview:
The Forensic Auditor specializes in investigating potential fraud and financial misconduct, using advanced analytical techniques to uncover irregularities.

Key Responsibilities:
- Conduct investigations into suspected fraud, waste, or abuse.
- Analyze financial data and transactions to detect anomalies.
- Prepare detailed forensic reports and assist in legal proceedings if necessary.
- Collaborate with legal, compliance, and external law enforcement agencies.

Requirements & Skills:
- Expertise in fraud detection and forensic accounting.
- Strong analytical skills and proficiency in data analysis techniques.
- Certifications such as Certified Fraud Examiner (CFE) are highly valued.
- Ability to work discreetly and handle sensitive information.`
  },
  {
    title: 'Risk Management Auditor',
    description: `Role Overview:
The Risk Management Auditor evaluates the organization’s risk management framework and processes, ensuring that risks are identified, assessed, and managed effectively.

Key Responsibilities:
- Assess the organization’s risk management processes across various operational and strategic areas.
- Identify potential risks and evaluate the adequacy of internal controls.
- Provide recommendations to enhance risk mitigation strategies.
- Work closely with risk management and business units to align audit findings with organizational risk profiles.

Requirements & Skills:
- In-depth understanding of risk assessment methodologies.
- Strong analytical and evaluative skills.
- Excellent communication skills to effectively report risks and recommendations.
- Professional certifications related to risk management and auditing are beneficial.`
  }
];

const JobDescriptions = () => {
  const [filterText, setFilterText] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    // Reset the selected role if it doesn't match the new filter
    if (selectedRole && !e.target.value.toLowerCase().includes(selectedRole.title.toLowerCase())) {
      setSelectedRole(null);
    }
  };

  const filteredRoles = rolesData.filter((role) =>
    role.title.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4">
        {/* Sidebar */}
        <div className="md:w-1/3 bg-white shadow rounded-lg p-4">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Internal Audit Roles</h2>
          <input
            type="text"
            placeholder="Filter roles..."
            value={filterText}
            onChange={handleFilterChange}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          />
          <ul className="space-y-2 max-h-96 overflow-y-auto">
            {filteredRoles.map((role, index) => (
              <li
                key={index}
                onClick={() => setSelectedRole(role)}
                className={`p-3 cursor-pointer rounded transition-colors duration-200 hover:bg-blue-100 ${
                  selectedRole && selectedRole.title === role.title
                    ? 'bg-blue-200'
                    : 'bg-gray-100'
                }`}
              >
                {role.title}
              </li>
            ))}
            {filteredRoles.length === 0 && (
              <li className="text-gray-500">No roles match your filter.</li>
            )}
          </ul>
        </div>
        {/* Role Description */}
        <div className="md:w-2/3 bg-white shadow rounded-lg p-4">
          {selectedRole ? (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-800">
                {selectedRole.title}
              </h2>
              <pre className="whitespace-pre-wrap text-gray-700">
                {selectedRole.description}
              </pre>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-600">
                Please select a role to view its job description.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDescriptions;
